import { useLoggedInCompany } from "@/hooks/queries/useCompanyQueries";
import { useTerminateUser } from "@/hooks/queries/useDepartmentMutations";
import { useInviteEmployees } from "@/hooks/queries/useEmployeeMutations";
import { useAuth } from "@/hooks/useAuth";
import { useMemo, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import EmployeeHeader from "@/components/employees/EmployeeHeader";
import EmployeeFilters from "@/components/employees/EmployeeFilters";
import EmployeeTable from "@/components/employees/EmployeeTable";
import InviteEmployeeModal from "@/components/employees/InviteEmployeeModal";
import { useDepartmentData } from "@/hooks/queries/useDepartmentData";

// Helper function to validate MongoDB ObjectId format (24 character hex string)
const isValidObjectId = (id: string): boolean => {
  return /^[0-9a-fA-F]{24}$/.test(id);
};

// Define the employee type from company data
interface CompanyEmployee {
  _id: string;
  fullName: string;
  email: string;
  role: string;
  departmentId: string;
  isActive: boolean;
  isApproved?: boolean;
}




const Employees = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const terminateUserMutation = useTerminateUser();
  const inviteEmployeesMutation = useInviteEmployees();

  // Check if user is department admin
  const isDepartmentAdmin = user?.role === "department_admin";

  // Get company data for company admins
  const {
    data: companyData,
    isLoading: companyLoading,
    error: companyError,
    refetch: companyRefetch,
  } = useLoggedInCompany(!isDepartmentAdmin);

  // Get all departments for company admins - using company data directly
  // const {
  //   data: allDepartments,
  //   isLoading: departmentsLoading,
  //   error: departmentsError,
  // } = useDepartments(companyData?.data?.document?._id || "");

  // Get department data for department admins
  const {
    data: departmentData,
    isLoading: departmentLoading,
    error: departmentError,
    refetch: departmentRefetch,
  } = useDepartmentData(
    user?.departmentId || "",
    isDepartmentAdmin && !!user?.departmentId
  );

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  // Validate user's departmentId on mount (for department admins)
  useEffect(() => {
    if (isDepartmentAdmin && user?.departmentId) {
      console.log("👤 Validating user's departmentId:", {
        departmentId: user.departmentId,
        isValid: isValidObjectId(user.departmentId),
        userRole: user.role,
        userName: user.fullName
      });
      
      if (!isValidObjectId(user.departmentId)) {
        console.error("❌ CRITICAL: User's departmentId is not a valid ObjectId:", user.departmentId);
        console.error("This will cause invitation failures. User object:", user);
      }
    }
  }, [isDepartmentAdmin, user?.departmentId, user]);

  // Process employees and departments from the fetched data
  const { employees, departments, isLoading, error } = useMemo(() => {
    if (isDepartmentAdmin) {
      // For department admins, use department data
      if (departmentLoading) {
        return { employees: [], departments: [], isLoading: true, error: null };
      }
      
      if (departmentError) {
        return { employees: [], departments: [], isLoading: false, error: departmentError.message };
      }
      
      if (departmentData?.data?.document) {
        const department = departmentData.data.document;
        const departmentEmployees: CompanyEmployee[] = department.employees.map((emp: any) => ({
          _id: emp.id,
          fullName: emp.name,
          email: emp.email,
          role: emp.role === "departmentAdmin" ? "department_admin" : "employee",
          departmentId: department._id,
          isActive: true,
          isApproved: true,
        }));
        
        return {
          employees: departmentEmployees,
          departments: [department],
          isLoading: false,
          error: null
        };
      }
      
      return { employees: [], departments: [], isLoading: false, error: null };
    } else {
      // For company admins, use company data directly
      if (companyLoading) {
        return { employees: [], departments: [], isLoading: true, error: null };
      }
      
      if (companyError) {
        return { employees: [], departments: [], isLoading: false, error: companyError.message };
      }
      
      if (companyData?.data?.document) {
        const company = companyData.data.document;
        
        // Process and validate departments to ensure they have proper _id fields
        const processedDepartments = (company.departments || []).map((dept: any) => {
          // If department has 'id' instead of '_id', use that
          const deptId = dept._id || dept.id;
          
          console.log("🏢 Processing department:", {
            originalDept: dept,
            hasUnderscoreId: !!dept._id,
            hasId: !!dept.id,
            name: dept.name,
            finalId: deptId,
            isValidObjectId: deptId ? isValidObjectId(deptId) : false
          });
          
          return {
            _id: deptId,
            name: dept.name
          };
        }).filter(dept => dept._id && isValidObjectId(dept._id)); // Only keep departments with valid ObjectIds
        
        console.log("Company data processed:", {
          companyName: company.name,
          totalDepartments: company.departments?.length || 0,
          validDepartments: processedDepartments.length,
          departments: processedDepartments,
          totalEmployees: company.employees?.length || 0
        });
        
        return {
          employees: company.employees || [],
          departments: processedDepartments,
          isLoading: false,
          error: null
        };
      }
      
      return { employees: [], departments: [], isLoading: false, error: null };
    }
  }, [isDepartmentAdmin, departmentData, departmentLoading, departmentError, companyData, companyLoading, companyError]);

  // Filter employees based on search term, role, and status
  const filteredEmployees = useMemo(() => {
    return employees.filter((employee) => {
      const matchesSearch = employee.fullName
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
        employee.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesRole = roleFilter === "all" || employee.role === roleFilter;
      const matchesStatus = statusFilter === "all" || 
        (statusFilter === "active" && employee.isActive) ||
        (statusFilter === "inactive" && !employee.isActive);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [employees, searchTerm, roleFilter, statusFilter]);

  // Handle employee termination
  const handleTerminateEmployee = async (employeeId: string) => {
    if (!window.confirm(t("admin.confirmTerminate", "Are you sure you want to terminate this employee?"))) {
      return;
    }

    try {
      await terminateUserMutation.mutateAsync(employeeId);
      toast.success(t("admin.employeeTerminated", "Employee terminated successfully"));
      
      // Refresh data
      if (isDepartmentAdmin) {
        departmentRefetch();
      } else {
        companyRefetch();
      }
    } catch (error: any) {
      console.error("Error terminating employee:", error);
      toast.error(error.response?.data?.message || t("admin.errorTerminating", "Failed to terminate employee"));
    }
  };

  // Handle employee invitation
  const handleInviteEmployees = async (emails: string[], selectedDepartmentId?: string) => {
    try {
      setIsInviting(true);
      
      // For department admins, use their department ID
      // For company admins, use the selected department ID
      const targetDepartmentId = isDepartmentAdmin 
        ? user?.departmentId 
        : selectedDepartmentId;

      if (!targetDepartmentId) {
        toast.error(t("admin.noDepartmentSelected", "No department selected"));
        return;
      }

      console.log("🔍 DEBUG - Sending invitation with:", {
        targetDepartmentId,
        isDepartmentAdmin,
        userDepartmentId: user?.departmentId,
        selectedDepartmentId,
        userRole: user?.role,
        isValidObjectId: isValidObjectId(targetDepartmentId)
      });

      // Validate that we're sending a valid ObjectId, not a department name
      if (!isValidObjectId(targetDepartmentId)) {
        console.error("❌ ERROR - Invalid department ID format:", targetDepartmentId);
        toast.error(
          t("admin.invalidDepartmentId", "Invalid department ID format. Please contact support.")
        );
        return;
      }

      await inviteEmployeesMutation.mutateAsync({
        departmentId: targetDepartmentId,
        emails: emails,
      });

      toast.success(t("admin.invitesSent", "Invitations sent successfully"));
      setShowInviteModal(false);
      
      // Refresh data
      if (isDepartmentAdmin) {
        departmentRefetch();
      } else {
        companyRefetch();
      }
    } catch (error: any) {
      console.error("Error sending invitations:", error);
      toast.error(error.response?.data?.message || t("admin.errorSendingInvites", "Failed to send invitations"));
    } finally {
      setIsInviting(false);
    }
  };


  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="h-16 w-16 animate-pulse-slow rounded-full bg-primary-500"></div>
          <h1 className="text-2xl font-bold text-primary-800 dark:text-primary-200">
            {t("common.loading", "Loading...")}
          </h1>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
            {t("common.error", "Error")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            {t("common.retry", "Retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <EmployeeHeader
        isDepartmentAdmin={isDepartmentAdmin}
        onInviteClick={() => setShowInviteModal(true)}
      />

      <EmployeeFilters
        searchTerm={searchTerm}
        roleFilter={roleFilter}
        statusFilter={statusFilter}
        onSearchChange={setSearchTerm}
        onRoleFilterChange={setRoleFilter}
        onStatusFilterChange={setStatusFilter}
      />

      <EmployeeTable
        filteredEmployees={filteredEmployees}
        departments={Array.isArray(departments) ? departments : []}
        onTerminateEmployee={handleTerminateEmployee}
        isDepartmentAdmin={isDepartmentAdmin}
      />

        <InviteEmployeeModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInviteEmployees}
          isSubmitting={isInviting}
          departmentName={isDepartmentAdmin ? departments[0]?.name : undefined}
          isDepartmentAdmin={isDepartmentAdmin}
          departments={departments}
          companyId={user?.companyId}
        />
    </div>
  );
};

export default Employees;
